/**
 * Reveal workflow I/O — enrich a redo customer into a person profile
 * (professional history, property/wealth, social presence).
 *
 * Contract owned here as Zod (redo Temporal convention: workflow I/O are Zod
 * schemas). The reveal engine (@redotech/reveal) produces the response; this is
 * the serializable boundary the workflow passes across.
 */
import * as z from "zod";

// ---------------------------------------------------------------------------
// input
// ---------------------------------------------------------------------------

export const RevealRequest = z.object({
  redoCustomerId: z.number().optional(), // absent for an ad-hoc name/address lookup
  email: z.string().optional(), // PII — must not be passed to the LLM
  phone: z.string().optional(), // PII — must not be passed to the LLM
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  address1: z.string().optional(),
  address2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  zip: z.string().optional(),
  age: z.number().optional(),
  netWorth: z.number().optional(), // known/seed value, if any
  dob: z.string().optional(), // ISO-8601
});
export type RevealRequest = z.infer<typeof RevealRequest>;

// ---------------------------------------------------------------------------
// output — shared sub-shapes
// ---------------------------------------------------------------------------

/** Where a signal came from + how fresh it is. */
export const Provenance = z.object({
  sources: z.array(z.string()), // cited URLs / provider names
  method: z.enum(["api", "web", "county-gis", "computed"]).optional(),
  asOf: z.string().optional(), // data vintage / fetch time (ISO-8601)
});
export type Provenance = z.infer<typeof Provenance>;

export const Job = z.object({
  title: z.string(),
  company: z.string(), // bare name — job history doesn't need the rich Company shape
  startDate: z.string().nullable(), // ISO or partial ("2019", "2019-03")
  endDate: z.string().nullable(), // null = current role
});
export type Job = z.infer<typeof Job>;

/** A company exit event (informs the "exit" net-worth component). */
export const CompanyExit = z.object({
  type: z.enum(["acquisition", "ipo"]),
  amountUsd: z.number().optional(),
  date: z.string().optional(), // ISO-8601
  acquirer: z.string().optional(),
});
export type CompanyExit = z.infer<typeof CompanyExit>;

/**
 * The person's CURRENT company as a first-class entity. Consolidates what used
 * to be scattered strings (currentCompany / companySynopsis / companyResearched)
 * and gives the funding/valuation/exit signals the net-worth model gathers a
 * structured home instead of free text. `name` + `confidence` are the person→
 * company resolution — surfaces a mismatch when the wrong company was matched.
 */
export const Company = z.object({
  name: z.string(),
  domain: z.string().optional(), // resolved website — the disambiguation anchor
  url: z.string().optional(), // LinkedIn / Crunchbase page
  synopsis: z.string().optional(),
  industry: z.string().optional(),
  stage: z.string().optional(), // e.g. "Series C", "public", "acquired", "bootstrapped"
  lastValuationUsd: z.number().optional(),
  lastFundingRound: z.string().optional(), // e.g. "Series C, $150M, 2023"
  exit: CompanyExit.optional(),
  provenance: Provenance.optional(),
  confidence: z.number().optional(), // name→company resolution confidence
});
export type Company = z.infer<typeof Company>;

export const Career = z.object({
  currentTitle: z.string(), // the person's role — stays on Career, not Company
  company: Company, // current company, consolidated (name/synopsis/valuation/exit/resolution)
  isFounder: z.boolean(),
  isInvestor: z.boolean(),
  isPublicPerson: z.boolean().optional(),
  jobHistory: z.array(Job),
  synopsis: z.string().optional(), // person-level synopsis
  matchedProfileUrl: z.string().optional(), // e.g. resolved LinkedIn URL
  provenance: Provenance.optional(),
  confidence: z.number().optional(),
});
export type Career = z.infer<typeof Career>;

/** Shared social-profile shape (followerCount: null = profile found, count not visible). */
const SocialProfile = z.object({
  profileUrl: z.string().optional(),
  handle: z.string().optional(),
  followerCount: z.number().nullable().optional(),
  verified: z.boolean().optional(),
  confidence: z.number().optional(),
});

export const TiktokData = SocialProfile;
export type TiktokData = z.infer<typeof TiktokData>;

export const InstagramData = SocialProfile;
export type InstagramData = z.infer<typeof InstagramData>;

export const TwitterData = SocialProfile.extend({
  followingCount: z.number().optional(),
});
export type TwitterData = z.infer<typeof TwitterData>;

/** YouTube reports SUBSCRIBERS; they ride in followerCount so the roll-up stays one sum. */
export const YoutubeData = SocialProfile;
export type YoutubeData = z.infer<typeof YoutubeData>;

export const SocialMediaData = z.object({
  tiktok: TiktokData.optional(),
  twitter: TwitterData.optional(),
  instagram: InstagramData.optional(),
  youtube: YoutubeData.optional(),
  totalFollowers: z.number().nullable().optional(), // rolled-up sum across visible profiles (null = none visible)
});
export type SocialMediaData = z.infer<typeof SocialMediaData>;

export const PropertyData = z.object({
  propertyType: z.enum(["business", "residential-owned", "residential-rent"]),
  homeMarketValue: z.number().optional(), // Redfin AVM / Zillow ZHVI, or county TOTAL_MKT_VALUE
  estimatedRent: z.number().optional(), // Zillow ZORI
  // County-GIS assessor records (wired in reveal via county-gis adapters):
  nameOnDeed: z.string().optional(), // owner of record
  homeAppraisedValue: z.number().optional(), // county assessed value
  lastSalePrice: z.number().optional(),
  homeDateOfPurchase: z.string().optional(), // last-sale date, ISO-8601
  lastAppraisalDate: z.string().optional(), // ISO-8601
  buildingSqft: z.number().optional(),
  beds: z.number().optional(),
  baths: z.number().optional(),
  yearBuilt: z.number().optional(),
  landUse: z.string().optional(), // assessor property class
  isPrimaryResidence: z.boolean().optional(),
  provenance: Provenance.optional(), // method="county-gis" carries FIPS/adapter/sourceUrl
  confidence: z.number().optional(),
});
export type PropertyData = z.infer<typeof PropertyData>;

/** One net-worth building block; totals are summed deterministically in code (never trust LLM arithmetic). */
export const NetWorthComponent = z.object({
  kind: z.enum([
    "exit",
    "current-equity",
    "compensation-savings",
    "real-estate",
    "other",
  ]),
  label: z.string(), // e.g. "Divvy → Bill.com acquisition ($2.5B, 2021)"
  liquidity: z.enum(["liquid", "paper"]),
  lowUsd: z.number(),
  midUsd: z.number(),
  highUsd: z.number(),
  assumptions: z.array(z.string()),
  sources: z.array(z.string()),
});
export type NetWorthComponent = z.infer<typeof NetWorthComponent>;

/** Structured, defensible net-worth estimate — a RANGE with a liquid/paper split, not a point guess. */
export const NetWorth = z.object({
  estimatedUsd: z.number(), // headline (== midUsd)
  lowUsd: z.number(),
  midUsd: z.number(),
  highUsd: z.number(),
  liquidMidUsd: z.number(),
  paperMidUsd: z.number(), // illiquid equity + real estate
  tier: z.string(), // e.g. "Tier 1: $10M+"
  rationale: z.string(), // one-paragraph explanation built from the components
  components: z.array(NetWorthComponent),
  confidence: z.number(),
  caveats: z.array(z.string()),
});
export type NetWorth = z.infer<typeof NetWorth>;

/** A recent piece of content matched to the person (podcast / video / post). */
export const MediaItem = z.object({
  kind: z.enum(["podcast", "youtube", "blog", "article", "other"]),
  title: z.string(),
  url: z.string(),
  publishedAt: z.string().optional(), // ISO-8601 when known
  matchReason: z.string().optional(), // why this is believed to be THIS person
  confidence: z.number().optional(),
});
export type MediaItem = z.infer<typeof MediaItem>;

/** Economic context for the person's ZIP — the denominator behind wealth inferences (Census/ACS). */
export const AreaContext = z.object({
  zip: z.string().optional(),
  medianHouseholdIncome: z.number().optional(),
  perCapitaIncome: z.number().optional(),
  pctHouseholds200kPlus: z.number().optional(),
  pctBachelorsPlus: z.number().optional(),
  pctOwnerOccupied: z.number().optional(),
  medianHomeValue: z.number().optional(),
  medianGrossRent: z.number().optional(),
  provenance: Provenance.optional(),
});
export type AreaContext = z.infer<typeof AreaContext>;

export const Profile = z.object({
  name: z.string().optional(),
  age: z.number().optional(),
  gender: z.string().optional(), // inferred (e.g. from first name) — treat as low-confidence
  nameRarityTier: z.string().optional(), // "unique" | "near-unique" | ... — identity-confidence signal
  predictedNetWorth: z.number().optional(), // scalar mirror of netWorth.estimatedUsd (quick access)
  netWorth: NetWorth.optional(), // full component-based estimate + rationale
  career: Career.optional(),
  propertyData: PropertyData.optional(),
  areaContext: AreaContext.optional(),
  socialMediaData: SocialMediaData.optional(),
  media: z.array(MediaItem).optional(), // recent podcasts / videos / posts by the person
});
export type Profile = z.infer<typeof Profile>;

// ---------------------------------------------------------------------------
// output
// ---------------------------------------------------------------------------

export const RevealResponse = z.object({
  schemaVersion: z.literal(1),
  generatedAt: z.string(), // ISO-8601
  requestId: z.string().nullable(),
  status: z.enum(["resolved", "partial", "not_found"]),
  confidence: z.number(),
  // Reveal match-confidence; unrelated to the restriction_confidence DB enum.
  //redontIgnore
  confidenceBand: z.enum(["high", "medium", "low"]),
  profile: Profile.nullable(), // null on not_found
});
export type RevealResponse = z.infer<typeof RevealResponse>;

// --- live progress: queryable while the workflow runs ---

/**
 * Per-step lifecycle. `running` is the one a poller wants to highlight;
 * `degraded`/`skipped` are settled outcomes that still let dependents proceed.
 */
export const RevealStepStatus = z.enum([
  "pending",
  "running",
  "ok",
  "degraded",
  "skipped",
]);
export type RevealStepStatus = z.infer<typeof RevealStepStatus>;

export const RevealStepProgress = z.object({
  name: z.string(), // plan step name, e.g. "property", "netWorth"
  status: RevealStepStatus,
});
export type RevealStepProgress = z.infer<typeof RevealStepProgress>;

/**
 * A snapshot of the reveal pipeline for a UI to render a real loading sequence
 * against, rather than a decorative timer. `steps` is in plan order.
 */
export const RevealProgress = z.object({
  startedAtMs: z.number(),
  elapsedMs: z.number(),
  settledCount: z.number(),
  totalCount: z.number(),
  done: z.boolean(),
  steps: z.array(RevealStepProgress),
});
export type RevealProgress = z.infer<typeof RevealProgress>;

/** Query the running workflow for its current pipeline progress. */
export const revealProgressQuery = {
  name: "getRevealProgress" as const,
  output: RevealProgress,
};

// --- workflow definition ---

export const revealCustomerWorkflow = {
  // Registered workflow type — must equal the exported key and the implementing
  // function's name, or the worker won't resolve it.
  name: "revealCustomerWorkflow" as const,
  input: RevealRequest,
  output: RevealResponse,
};
